import { WmChart, WmChartSlice } from "@watermarkinsights/ripple-react";
import translate from "@/i18n/translate";
import { useIntl } from "react-intl";
import React, { useReducer } from "react";
import { openFlyout } from "../../static/js/commons";
import OpenDrillDownViewSll from "../sll/OpenDrillDownViewSll";
import { StudentAssessmentProgressByTerm } from "./Sll";

interface AssessmentProgressChartState {
  isDrilldownOpen: boolean;
  drilldownType: string;
  drilldownName: string;
  termUuid: string;
}

interface AssessmentProgressChartAction {
  type: string;
  drilldownName: string;
  termUuid: string;
}

const defaultAssessmentProgressChartAction = {
  type: "",
  drilldownName: "",
  termUuid: ""
};

const reducer = (
  state: AssessmentProgressChartState,
  action: AssessmentProgressChartAction
) => {
  switch (action.type) {
    case "ToggleDrilldown":
      return { ...state, isDrilldownOpen: !state.isDrilldownOpen };
    case "SetDrilldownData":
      return {
        ...state,
        drilldownName: action.drilldownName,
        termUuid: action.termUuid
      };
    case "OpenDrilldown":
      return { ...state, isDrilldownOpen: true, drilldownName: action.drilldownName, termUuid: action.termUuid };
    case "CloseDrilldown":
      return { ...state, isDrilldownOpen: false, drilldownName: "", termUuid: "" };
    default:
      return state;
  }
};

export interface AssessmentProgressChartProps {
  assessmentProgress: StudentAssessmentProgressByTerm[];
  noActivityTerms: string;
}

export default function AssessmentProgressChart({
  assessmentProgress,
  noActivityTerms
}: AssessmentProgressChartProps) {
  const initialState = {
    isDrilldownOpen: false,
    drilldownType: "",
    drilldownName: "",
    termUuid: ""
  };

  const awaitingSubmission = useIntl().formatMessage({
    id: "app.assessmentProgressChart.awaitingSubmission"
  });
  const awaitingAssessment = useIntl().formatMessage({
    id: "app.assessmentProgressChart.awaitingAssessment"
  });
  const done = useIntl().formatMessage({
    id: "app.done"
  });

  const POPOVER_BUTTON_TEXT = useIntl().formatMessage({
    id: "app.assessmentProgressChart.drilldownCommonAssessment"
  });

  const DD_SUB_HEADER = useIntl().formatMessage({
    id: "app.assessmentProgressChart.studentAssessmentByTerm"
  });

  const setIsDrilldownOpen = () => {
    dispatch({
      ...defaultAssessmentProgressChartAction,
      type: "ToggleDrilldown"
    });
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const openDrilldownView = (name: string, termUuuid: string) => {
    // setIsDrilldownOpen();
    openDrilldown(name, termUuuid);
    dispatch({
      ...defaultAssessmentProgressChartAction,
      type: "SetDrilldownData",
      drilldownName: name,
      termUuid: termUuuid
    });
  };

  const openDrilldown = (name: string, termUuuid: string) => {
    dispatch({
      ...defaultAssessmentProgressChartAction,
      type: "OpenDrilldown",
      drilldownName: name,
      termUuid: termUuuid
    });
  };

  const closeDrilldown = () => {
    dispatch({
      ...defaultAssessmentProgressChartAction,
      type: "CloseDrilldown"
    });
  };

  function useStudentAwaitingSubmission(
    asstProgress: StudentAssessmentProgressByTerm
  ) {
    return useIntl().formatMessage(
      { id: "app.assessmentProgressChart.popoverStatusText" },
      { statusLabel: awaitingSubmission, labelText: asstProgress?.name }
    );
  }

  const StudentAwaitingSubmission = (
    asstProgress: StudentAssessmentProgressByTerm
  ) => useStudentAwaitingSubmission(asstProgress);

  function useTotalText(
    asstProgress: StudentAssessmentProgressByTerm,
    total: string
  ) {
    return useIntl().formatMessage(
      { id: "app.assessmentProgressChart.total" },
      { asstProgressName: asstProgress?.name, total: total }
    );
  }

  const TotalText = (
    asstProgress: StudentAssessmentProgressByTerm,
    total: string
  ) => useTotalText(asstProgress, total);

  function useStudentAssessmentDone(
    asstProgress: StudentAssessmentProgressByTerm
  ) {
    return useIntl().formatMessage(
      { id: "app.assessmentProgressChart.popoverStatusText" },
      { statusLabel: done, labelText: asstProgress?.name }
    );
  }

  const StudentAssessmentDone = (
    asstProgress: StudentAssessmentProgressByTerm
  ) => useStudentAssessmentDone(asstProgress);

  function useStudentAssessmentAwaiting(
    asstProgress: StudentAssessmentProgressByTerm
  ) {
    return useIntl().formatMessage(
      { id: "app.assessmentProgressChart.popoverStatusText" },
      { statusLabel: awaitingAssessment, labelText: asstProgress?.name }
    );
  }

  const StudentAssessmentAwaiting = (
    asstProgress: StudentAssessmentProgressByTerm
  ) => useStudentAssessmentAwaiting(asstProgress);

  return (
    <div className="chart chart-section" id="assessment-progress-chart">
      <div className="progress-term-label">
        {translate("app.assessmentProgressChart.studentAssessmentProgress")}
      </div>
      <div className="legend-wrapper">
        <div className="legend" aria-hidden="true">
          <div className="legend-item">
            <div className="legend-color legend1 -legend-wrap"></div>
            <div className="legend-text">
              {translate("app.assessmentProgressChart.awaitingSubmission")}
            </div>
          </div>
          <div className="legend-item">
            <div className="legend-color legend2 -legend-wrap"></div>
            <div className="legend-text">
              {translate("app.assessmentProgressChart.awaitingAssessment")}
            </div>
          </div>
          <div className="legend-item">
            <div className="legend-color legend3"></div>
            <div className="legend-text">{translate("app.done")}</div>
          </div>
        </div>
      </div>
      {assessmentProgress.map(
        (asstProgress: StudentAssessmentProgressByTerm) => {
          const total = Number(
            Object.values(asstProgress.studentAssessments).reduce(
              (newVal: number, oldVal: number) => (newVal = newVal + oldVal),
              0
            )
          );

          return (
            <div key={String(asstProgress?.name)}>
              <div className="chart-space">
                <WmChart
                  chart-type="bar4"
                  value-format="amount"
                  show-legend="false"
                  label={String(TotalText(asstProgress, String(total)))}
                  onWmChartPopoverButtonTriggered={(e) =>
                    openDrilldownView(
                      String(asstProgress?.name),
                      String(asstProgress?.uuid)
                    )
                  }
                >
                  <WmChartSlice
                    legend={awaitingSubmission}
                    amount={String(
                      asstProgress.studentAssessments.awaitingSubmission
                    )}
                    popoverText={String(
                      StudentAwaitingSubmission(asstProgress)
                    )}
                    popover-title={
                      asstProgress.studentAssessments.awaitingSubmission +
                      "/" +
                      total
                    }
                    popoverButtonText={POPOVER_BUTTON_TEXT}
                  />
                  <WmChartSlice
                    legend={awaitingAssessment}
                    amount={String(
                      asstProgress.studentAssessments.awaitingAssessment
                    )}
                    popoverText={String(
                      StudentAssessmentAwaiting(asstProgress)
                    )}
                    popover-title={
                      asstProgress.studentAssessments.awaitingAssessment +
                      "/" +
                      total
                    }
                    popoverButtonText={POPOVER_BUTTON_TEXT}
                  />
                  <WmChartSlice
                    legend={done}
                    amount={String(asstProgress.studentAssessments.done)}
                    popoverText={String(StudentAssessmentDone(asstProgress))}
                    popover-title={
                      asstProgress.studentAssessments.done + "/" + total
                    }
                    popoverButtonText={POPOVER_BUTTON_TEXT}
                  />
                </WmChart>
              </div>
            </div>
          );
        }
      )}

      <div className="no-activity-label">
        {noActivityTerms === "" ? (
          ""
        ) : (
          <div className="-label">
            {translate("app.assessmentProgressChart.NoActivities", {
              noActivityTerms: noActivityTerms
            })}
          </div>
        )}
      </div>
      <OpenDrillDownViewSll
        setIsDrilldownOpen={closeDrilldown}
        drilldownName={state.drilldownName}
        subHeader={DD_SUB_HEADER}
        termUuid={state.termUuid}
        isVisible={state.isDrilldownOpen}
        open={state.isDrilldownOpen}
        id="sll-assessment-drill-down-view"
      ></OpenDrillDownViewSll>
    </div>
  );
}
